import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Building2, Percent, TrendingUp,
  BarChart3, AlertTriangle, Lock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

function fmt(v: number, decimals = 2): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
}

function aprColor(apr: number): string {
  if (apr < 18) return "#22c55e";
  if (apr < 24) return "#f59e0b";
  return "#ef4444";
}

function statusBadge(status: string | null | undefined) {
  if (status === "active") return <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">Active</Badge>;
  if (status === "canceling") return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800">Canceling</Badge>;
  if (status === "canceled") return <Badge variant="outline" className="text-muted-foreground">Cancelled</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>;
}

function AprTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold mb-1">{d.name}</p>
      <p className="text-muted-foreground">Effective APR: <span className="font-mono font-bold text-foreground">{fmt(d.apr)}%</span></p>
    </div>
  );
}

export default function SalespersonDealerView() {
  const [location, navigate] = useLocation();
  const dealerId = location.split("/salesperson/dealer/")[1]?.split("/")[0] ?? "";

  const { data, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/salesperson/dealer", dealerId],
    queryFn: () => fetch(`/api/salesperson/dealer/${dealerId}`, { credentials: "include" }).then((r) => {
      if (!r.ok) throw new Error("Failed to load dealer data");
      return r.json();
    }),
    retry: false,
    enabled: !!dealerId,
  });

  const dealerName = data?.companyName ?? data?.username ?? "Dealer";
  const subscriptionStatus = data?.subscriptionStatus;
  const isCanceling = subscriptionStatus === "canceling";

  return (
    <div className="min-h-screen bg-background">
      {/* Read-only banner */}
      <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 px-6 py-2.5">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300" data-testid="banner-read-only">
              Read-Only View — {isLoading ? "Loading..." : dealerName}
            </span>
            {!isLoading && isCanceling && (
              <span className="flex items-center gap-1 text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 rounded-full px-2 py-0.5 ml-1">
                <AlertTriangle className="w-3 h-3" />Churn Risk
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/salesperson")}
            className="gap-1.5 h-7 text-xs border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            data-testid="button-back-to-portal"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Back to Portal
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        ) : isError ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <AlertTriangle className="w-8 h-8 text-muted-foreground mx-auto" />
              <p className="text-sm font-medium">Unable to load dealer data</p>
              <p className="text-xs text-muted-foreground">This dealer may not be attributed to you, or an error occurred.</p>
              <Button size="sm" variant="outline" onClick={() => navigate("/salesperson")} className="gap-1.5 mt-2" data-testid="button-return-to-portal">
                <ArrowLeft className="w-4 h-4" />Return to Portal
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Dealer header */}
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-muted-foreground">
                    {dealerName.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{dealerName}</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    {statusBadge(subscriptionStatus)}
                    {data.email && <span className="text-xs text-muted-foreground">{data.email}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription + billing summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="dealer-stat-subscription">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-muted-foreground font-medium mb-1.5">Subscription</p>
                  <div className="mt-1">{statusBadge(subscriptionStatus)}</div>
                </CardContent>
              </Card>
              <Card data-testid="dealer-stat-mrr">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-muted-foreground font-medium mb-1.5">Monthly Value</p>
                  <p className="text-2xl font-bold">
                    {data.currentMrr != null
                      ? `$${Number(data.currentMrr).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : <span className="text-muted-foreground">Unavailable</span>}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="dealer-stat-lenders">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-muted-foreground" /></div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Active Lenders</p>
                  <p className="text-2xl font-bold">{data.lenders?.length ?? 0}</p>
                </CardContent>
              </Card>
              <Card data-testid="dealer-stat-statements">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-muted-foreground" /></div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Statements</p>
                  <p className="text-2xl font-bold">{data.statementCount ?? 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Churn risk warning */}
            {isCanceling && (
              <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-950/20" data-testid="card-churn-warning">
                <CardContent className="pt-5 pb-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">Churn Risk — Subscription Canceling</p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                      This dealer's subscription is in the canceling state. Their account will lose access at the end of the current billing period.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lenders */}
            {data.lenders && data.lenders.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" />Floor Plan Lenders
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground">Lender</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">APR</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Avg Units/Mo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.lenders.map((lender: any) => (
                          <tr key={lender.id} className="border-b last:border-0" data-testid={`lender-row-${lender.id}`}>
                            <td className="px-4 py-3 font-medium">{lender.name}</td>
                            <td className="px-4 py-3 text-right font-mono">
                              {lender.aprRate ? `${parseFloat(lender.aprRate).toFixed(2)}%` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {lender.avgUnitsPerMonth ? parseFloat(lender.avgUnitsPerMonth).toFixed(1) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* APR chart */}
            {data.aprByLender && data.aprByLender.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Percent className="w-4 h-4 text-primary" />
                    Effective APR by Lender
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Actual annualized rate including interest + fees from uploaded statements</p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.aprByLender} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={42} />
                      <Tooltip content={<AprTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                      <Bar dataKey="apr" radius={[4, 4, 0, 0]} maxBarSize={64}>
                        {data.aprByLender.map((entry: any, i: number) => (
                          <Cell key={i} fill={aprColor(entry.apr)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 px-1">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#22c55e] shrink-0" />{"<"} 18% — Good
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#f59e0b] shrink-0" />18–24% — Watch
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#ef4444] shrink-0" />{"> 24%"} — High
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Operational metrics */}
            {data.opMetrics && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />Operational Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {data.opMetrics.avgDaysToSell != null && (
                      <div data-testid="op-avg-days">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Avg Days to Sell</p>
                        <p className="text-xl font-bold mt-1">{parseFloat(data.opMetrics.avgDaysToSell).toFixed(0)}</p>
                      </div>
                    )}
                    {data.opMetrics.totalGrossProfit != null && (
                      <div data-testid="op-gross-profit">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Gross Profit</p>
                        <p className="text-xl font-bold mt-1">${parseFloat(data.opMetrics.totalGrossProfit).toLocaleString()}</p>
                      </div>
                    )}
                    {data.opMetrics.totalUnitsSold != null && (
                      <div data-testid="op-units-sold">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Units Sold</p>
                        <p className="text-xl font-bold mt-1">{data.opMetrics.totalUnitsSold}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No data state */}
            {(!data.lenders || data.lenders.length === 0) && !data.opMetrics && (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm font-medium">No data available yet</p>
                  <p className="text-xs text-muted-foreground mt-1">This dealer hasn't added lenders or uploaded any statements yet.</p>
                </CardContent>
              </Card>
            )}

            {/* Read-only note */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 border-t">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              <span>You are viewing this dealer's dashboard in read-only mode. No changes can be made from this view.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
